/**
 * Le point final du parcours PUBLIC : la candidature est déposée, l'invitation
 * à l'organisation est partie, et la suite se passe sur Discord.
 *
 * 🩸 C'EST LE SEUL ÉCRAN QUE VOIT UN CANDIDAT QUI VIENT DE LA PUB, et il
 * s'affiche sur TOUS les appareils — c'est ce qui le distingue de son ancêtre.
 * Il était le mur « viens sur un ordinateur », donc réservé au téléphone. Le
 * 19-08-2026 Kayla a tranché : « une fois qu'il a rempli le formulaire, on
 * l'invite dans l'organisation. Il a juste une page qui lui dit qu'une
 * invitation lui a été envoyée […] et c'est sur Discord que je vais mettre le
 * lien du test. » Le formulaire ne débouche donc plus sur la vérif technique
 * ni sur l'épreuve, sur aucun appareil : il débouche ICI.
 *
 * ⛔ NE PAS Y REMETTRE LE LIEN DU PARCOURS, ni bouton copier, ni partage natif.
 * Ils répondaient à un problème mort : le candidat n'a plus rien à garder, il
 * a une invitation par e-mail et son lien de test lui sera donné sur le
 * Discord. Deux chemins vers le test, c'est un de trop — et c'est celui que
 * Kayla ne contrôle pas.
 *
 * Le mur PC, lui, n'a pas disparu : il vit dans `EcranOrdinateur` et ne couvre
 * plus que les écrans de l'ÉPREUVE (`ECRANS_PC`), atteints par le lien que
 * Kayla poste sur Discord.
 *
 * ⛔ NI LE LIEN DU SERVEUR DISCORD. Il a été mis ici le 19-08 au matin, puis
 * RETIRÉ le soir même sur décision de Kayla : « on n'est pas censé les inviter
 * sur le Discord. Le Discord, il est censé le voir quand il s'est connecté sur
 * le cockpit. » Le bouton public créait un second parcours — des gens sur le
 * serveur qui n'étaient dans aucune organisation, donc introuvables, qui
 * ouvraient des tickets pour réclamer un lien.
 *
 * 🪤 Et il n'était pas nécessaire : l'OAuth du cockpit demande `guilds.join`
 * et le callback fait `PUT /guilds/{id}/members/{user}` avec le token du bot
 * (cf `convex/http.ts`) — **c'est le cockpit qui ajoute le candidat au
 * serveur**. Personne n'a besoin d'une invitation publique pour y arriver.
 *
 * ✅ CE QUI LE REMPLACE : le bouton d'ACTIVATION, qui ouvre le lien
 * d'acceptation de l'invitation — le même que celui contenu dans l'e-mail.
 * Il ne crée aucun second parcours : il mène exactement là où l'e-mail
 * menait, une porte plus tôt. C'est la réponse au vrai chiffre du 19-08 —
 * 35 candidatures, 29 invitations jamais ouvertes : le candidat est DEVANT
 * nous à cet instant, l'envoyer fouiller ses spams est là qu'on le perd.
 * L'e-mail continue de partir, il devient le filet et non le chemin.
 *
 * L'ordre voulu par Kayla, et celui que cet écran sert :
 *   activer son compte → Discord (dans le cockpit) → le test → le cockpit.
 *
 * ✅ LA SUITE EN TROIS ÉTAPES, DITE D'AVANCE (Kayla, 06-10-2026). La campagne
 * relancée ramène du trafic, et le candidat découvrait l'obligation Discord à
 * la porte du cockpit. Les trois étapes sont écrites ici : activer, connecter
 * son Discord dans le cockpit (c'est lui qui le fait entrer sur le serveur),
 * puis sur le Discord un ticket pour postuler : Francis envoie le test dans le
 * ticket, puis donne le shift (décision du 06-10-2026, le soir même). Du TEXTE seulement : toujours aucun lien du test ni
 * bouton « Rejoindre le Discord » (cf ci-dessus).
 */
import { Alerte, Button, Spinner } from "@/shared/ui";
import { ArrowRight, Check, MailPlus } from "@/shared/ui/icons";

import type { EtatInvitation } from "../RegieTestCandidatePage";

import { CANDIDATE_THEME } from "./PhoneFrame";

/** Le statut de l'invitation : pleine largeur de la colonne (400 px), le texte à gauche dans la page centrée. */
const CLASSE_ALERTE = "w-full max-w-[400px] text-left text-[14.5px]";

interface EcranCandidatureDeposeeProps {
  invitation: EtatInvitation;
}

export function EcranCandidatureDeposee({
  invitation,
}: EcranCandidatureDeposeeProps) {
  const urlActivation =
    invitation.kind === "ok" ? invitation.urlActivation : null;

  return (
    <main
      style={CANDIDATE_THEME}
      data-theme="glass"
      className="dark flex min-h-dvh flex-col items-center justify-center gap-5 bg-canvas px-5 py-10 text-center text-fg"
    >
      <Check aria-hidden weight="bold" className="h-10 w-10 text-valide" />

      <h1 className="max-w-[400px] text-[26px] font-extrabold leading-tight tracking-tight">
        Ta candidature est enregistrée
      </h1>

      {/* L'état de l'invitation est un STATUT : une `Alerte` de la librairie (le ton dit le sens), plus une
          carte dont l'icône prend la couleur à la main. */}
      {invitation.kind === "en_cours" && (
        <Alerte
          ton="info"
          icone={<Spinner taille="sm" decoratif />}
          texte="Envoi de ton invitation…"
          className={CLASSE_ALERTE}
        />
      )}

      {/* 🩸 L'E-MAIL A CESSÉ D'ÊTRE LE TITRE LE 20-08-2026, ET C'EST UN
          CORRECTIF. Cet écran annonçait « Ton invitation part par e-mail »
          en gras, le bouton d'activation en dessous. Résultat mesuré la nuit
          même : Faniah entre par le bouton en 56 secondes, crée son compte,
          lie son Discord, passe le test — puis ouvre un ticket pour réclamer
          « le mail » qu'elle n'a jamais eu à attendre. Elle avait franchi la
          porte sans comprendre que c'ÉTAIT la porte.

          Le geste passe donc devant, et l'e-mail derrière, en filet. */}
      {invitation.kind === "ok" &&
        (urlActivation ? (
          <Alerte
            ton="ok"
            titre="Il te reste une étape : activer ton compte"
            className={CLASSE_ALERTE}
            texte={
              <>
                Clique le bouton ci-dessous — c'est tout.{" "}
                <b>Tu n'as aucun e-mail à attendre.</b>
                <span className="mt-1.5 block text-muted">
                  On t'en a aussi envoyé un à {invitation.email}, au cas où tu
                  fermerais cette page.
                </span>
              </>
            }
          />
        ) : (
          <Alerte
            ton="ok"
            icone={<MailPlus />}
            titre="Ton invitation part par e-mail"
            className={CLASSE_ALERTE}
            texte={
              <>
                À <b>{invitation.email}</b>. Clique le lien dedans pour créer
                ton compte.
                <span className="mt-1.5 block text-muted">
                  Rien au bout de quelques minutes ? Vérifie tes spams.
                </span>
              </>
            }
          />
        ))}

      {/* 🩸 Le candidat revient sur son lien après être entré. Sans ce cas
          il lisait « Ton invitation n'a pas pu partir » alors qu'il est
          déjà membre — la création d'une invitation neuve échoue en 400,
          puisqu'il en a déjà une, acceptée. */}
      {invitation.kind === "deja_membre" && (
        <Alerte
          ton="ok"
          titre="Ton compte est déjà activé"
          texte="Tu n'as plus rien à faire ici — connecte-toi pour continuer."
          className={CLASSE_ALERTE}
        />
      )}

      {/* Ambre et non rouge : l'invitation n'est pas partie, mais la candidature, elle, est bien enregistrée. */}
      {invitation.kind === "echec" && (
        <Alerte
          ton="attention"
          icone={<MailPlus />}
          titre="Ton invitation n'a pas pu partir"
          texte="Ta candidature est bien enregistrée — on te recontacte."
          className={CLASSE_ALERTE}
        />
      )}

      {/* Le geste principal de l'écran : il se fait MAINTENANT, sur le
          téléphone qui vient de remplir le formulaire. C'est la même porte que
          l'e-mail, franchie une étape plus tôt — jamais un second parcours. */}
      {urlActivation && (
        <Button
          href={urlActivation}
          size="xl"
          variant="accent"
          className="w-full max-w-[400px] justify-center"
          iconRight={<ArrowRight aria-hidden className="h-5 w-5" />}
        >
          Activer mon compte
        </Button>
      )}

      {/* Déjà entré : le seul geste qui lui reste est de se connecter. Sans ce
          lien il devrait retaper l'adresse du cockpit de tête. */}
      {invitation.kind === "deja_membre" && (
        <Button
          href="/"
          size="xl"
          variant="accent"
          className="w-full max-w-[400px] justify-center"
          iconRight={<ArrowRight aria-hidden className="h-5 w-5" />}
        >
          Me connecter
        </Button>
      )}

      {urlActivation ? (
        <ol className="flex w-full max-w-[400px] flex-col gap-2.5 text-left text-[14px] leading-relaxed text-muted">
          <li>
            <b className="text-fg">1. Active ton compte</b> avec le bouton ci-dessus.
          </li>
          <li>
            <b className="text-fg">2. Connecte ton Discord</b> dans le cockpit — c'est
            lui qui te fait entrer sur le serveur.
          </li>
          <li>
            <b className="text-fg">3. Sur le Discord</b> : ouvre un ticket pour
            postuler — Francis t'envoie ton test, puis ton shift.
          </li>
        </ol>
      ) : (
        <p className="max-w-[340px] text-[13px] leading-relaxed text-muted">
          Tu peux fermer cette page.
        </p>
      )}
    </main>
  );
}
